import { createRequestAction, ErrorPayload } from 'helpers/actionHelpers/createRequestAction'
import {
  CCOnboardingPatchApiRequest,
  CCDetailedOnboardingStateApiResponse,
  CCOnboardingStateApiResponse,
  CCPersonDataApiRequest,
  CCLedger,
  CreditCard,
  CCBaseApiResponse,
  CCUid,
  CCPaymentMethod,
  CCCreatePaymentMethodApiRequest,
  CCPaymentMethodUid,
  CCOneTimePayment,
  CCCreatePaymentApiRequest,
  CCCreateAutoPaymentApiRequest,
  CCAutoPayment,
  CCPaymentUid,
  CCPayment,
} from 'components/features/creditCard/creditCardStore/types'
import { createAction } from 'helpers/actionHelpers/createAction'
import { PhoneVerifyApiRequest, PhoneVerifyApiResponse } from 'components/features/creditCard/creditCardStore/endpoints'
import { Collection } from 'interfaces'
import { Transaction } from 'interfaces/moneyflow'
import { CCReplaceReason } from 'components/features/creditCard/creditCardStore/types'
import { AddressApiResponse } from 'interfaces/locations'

type GetCCStateResponse =
  & Pick<CCOnboardingStateApiResponse, 'onboarding' | 'rewards'>
  & {
    ledgers: Collection<CCLedger, 'uid'>
    cards: Collection<CreditCard, 'uid'>
    transactions: Collection<Transaction, 'uid'>
  }

export class JoinCCWaitlistAction extends createRequestAction<'JOIN_CC_WAITLIST', void, void>('JOIN_CC_WAITLIST') {}

export class GetCCStateAction extends createRequestAction<'GET_CC_STATE', void, GetCCStateResponse>('GET_CC_STATE') {}
export class GetCCPaymentMethodsAction extends createRequestAction<'GET_CC_PAYMENT_METHODS', void, Collection<CCPaymentMethod, 'uid'>>('GET_CC_PAYMENT_METHODS') {}
export class SetCCPaymentMethodsAction extends createAction<'SET_CC_PAYMENT_METHODS', Collection<CCPaymentMethod, 'uid'>>('SET_CC_PAYMENT_METHODS') {}
export class GetCCPaymentsAction extends createRequestAction<'GET_CC_PAYMENTS', void, Collection<Transaction, 'uid'>>('GET_CC_PAYMENTS') {}
export class GetCCUpcomingPaymentsAction extends createRequestAction<'GET_CC_UPCOMING_PAYMENTS', void, Collection<CCPayment, 'uid'>>('GET_CC_UPCOMING_PAYMENTS') {}
export class GetCCOnboardingStateAction extends createRequestAction<'GET_CC_ONBOARDING_STATE', void, CCDetailedOnboardingStateApiResponse>('GET_CC_ONBOARDING_STATE') {}

type CreateCCPaymentMethodActionPayload = {
    paymentMethod: CCCreatePaymentMethodApiRequest

    // If payment method is added during card payment process
    paymentMode?: 'autoPay' | 'payCard'
  }

export class CreateCCPaymentMethodAction extends createRequestAction<'CREATE_CC_PAYMENT_METHOD', CreateCCPaymentMethodActionPayload, CCPaymentMethod>('CREATE_CC_PAYMENT_METHOD') {}
export class DeleteCCPaymentMethodAction extends createRequestAction<'DELETE_CC_PAYMENT_METHOD', CCPaymentMethodUid, {}>('DELETE_CC_PAYMENT_METHOD') {}
export class ResetCreateCCPaymentMethodAction extends createAction<'RESET_CREATE_CC_PAYMENT_METHOD', void>('RESET_CREATE_CC_PAYMENT_METHOD') {}
export class ResetDeleteCCPaymentMethodAction extends createAction<'RESET_DELETE_CC_PAYMENT_METHOD', void>('RESET_DELETE_CC_PAYMENT_METHOD') {}

export class CreateCCPaymentAction extends createRequestAction<'CREATE_CC_PAYMENT', CCCreatePaymentApiRequest, CCOneTimePayment>('CREATE_CC_PAYMENT') {}
export class DeleteCCPaymentAction extends createRequestAction<'DELETE_CC_PAYMENT', CCPaymentUid, {}>('DELETE_CC_PAYMENT') {}
export class ResetCreateCCPaymentAction extends createAction<'RESET_CREATE_CC_PAYMENT', void>('RESET_CREATE_CC_PAYMENT') {}
export class ResetDeleteCCPaymentAction extends createAction<'RESET_DELETE_CC_PAYMENT', void>('RESET_DELETE_CC_PAYMENT') {}

export class CreateCCAutoPaymentAction extends createRequestAction<'CREATE_CC_AUTO_PAYMENT', CCCreateAutoPaymentApiRequest, CCAutoPayment>('CREATE_CC_AUTO_PAYMENT') {}
export class DeleteCCAutoPaymentAction extends createRequestAction<'DELETE_CC_AUTO_PAYMENT', void, void>('DELETE_CC_AUTO_PAYMENT') {}
export class ResetCreateCCAutoPaymentAction extends createAction<'RESET_CREATE_CC_AUTO_PAYMENT', void>('RESET_CREATE_CC_AUTO_PAYMENT') {}
export class ResetDeleteCCAutoPaymentAction extends createAction<'RESET_DELETE_CC_AUTO_PAYMENT', void>('RESET_DELETE_CC_AUTO_PAYMENT') {}

export class PatchCCOnboardingPropsAction extends createRequestAction<'PATCH_CC_ONBOARDING_PROPS', CCOnboardingPatchApiRequest, CCDetailedOnboardingStateApiResponse>('PATCH_CC_ONBOARDING_PROPS') {}
export class PatchCCOnboardingAddressAction extends createAction<'PATCH_CC_ONBOARDING_ADDRESS', CCOnboardingPatchApiRequest>('PATCH_CC_ONBOARDING_ADDRESS') {}
export class PatchCCOnboardingSSNAction extends createAction<'PATCH_CC_ONBOARDING_SSN', CCOnboardingPatchApiRequest>('PATCH_CC_ONBOARDING_SSN') {}

type CreateCCPhoneActionPayload =
  & Omit<CCOnboardingPatchApiRequest, 'person_data'>
  & {
    person_data:
      & Omit<CCOnboardingPatchApiRequest['person_data'], 'phone_number'>
      & {
        phone_number: CCPersonDataApiRequest['phone_number']
      }
  }
type CreateCCPhoneActionResponse =
  & PhoneVerifyApiResponse
  & {
    'x-ratelimit-reset'?: string | null
  }
type CreateCCPhoneErrorWithRateLimits =
  & ErrorPayload
  & {
    'x-ratelimit-reset'?: string | null
  }
export class CreateCCPhoneAction extends createRequestAction<'CREATE_CC_PHONE', CreateCCPhoneActionPayload, CreateCCPhoneActionResponse, CreateCCPhoneErrorWithRateLimits>('CREATE_CC_PHONE') {}

type VerifyCCOnboardingPhoneActionPayload = {
  verify: PhoneVerifyApiRequest
  patch: CreateCCPhoneActionPayload
}
export class VerifyCCPhoneAction extends createRequestAction<'VERIFY_CC_PHONE', VerifyCCOnboardingPhoneActionPayload, PhoneVerifyApiResponse>('VERIFY_CC_PHONE') {}
export class ResetCCPhoneVerificationAction extends createAction<'RESET_CC_PHONE_VERIFICATION', void>('RESET_CC_PHONE_VERIFICATION') {}

export type DeviceId = string | null
export class CCOnboardingInitiateAction extends createRequestAction<'CC_ONBOARDING_INITIATE', DeviceId, CCDetailedOnboardingStateApiResponse>('CC_ONBOARDING_INITIATE') {}
export class CCOnboardingAcceptAction extends createRequestAction<'CC_ONBOARDING_ACCEPT', void, CCDetailedOnboardingStateApiResponse>('CC_ONBOARDING_ACCEPT') {}

export type WithCardUid = { uid: CCUid }
export class CCActivateCardAction extends createRequestAction<'CC_ACTIVATE_CARD', WithCardUid, CCBaseApiResponse>('CC_ACTIVATE_CARD') {}
export class CCReplaceCardAction extends createRequestAction<'CC_REPLACE_CARD', { reason: CCReplaceReason } & WithCardUid, CCBaseApiResponse>('CC_REPLACE_CARD') {}
export class CCLockCardAction extends createRequestAction<'CC_LOCK_CARD', WithCardUid, CCBaseApiResponse>('CC_LOCK_CARD') {}
export class CCUnlockCardAction extends createRequestAction<'CC_UNLOCK_CARD', WithCardUid, CCBaseApiResponse>('CC_UNLOCK_CARD') {}
export class ResetCCLockCardAction extends createAction<'RESET_CC_LOCK_CARD', void>('RESET_CC_LOCK_CARD') {}

export class GetCCShipmentAddressAction extends createRequestAction<'GET_CARD_SHIPMENT_ADDRESS', void, AddressApiResponse>('GET_CARD_SHIPMENT_ADDRESS') {}

export type CCOnboardingActions =
  | typeof JoinCCWaitlistAction.Type

  | typeof GetCCStateAction.Type
  | typeof GetCCOnboardingStateAction.Type
  | typeof PatchCCOnboardingPropsAction.Type
  | PatchCCOnboardingAddressAction
  | PatchCCOnboardingSSNAction
  | typeof CCOnboardingInitiateAction.Type
  | typeof CCOnboardingAcceptAction.Type

  | typeof CreateCCPhoneAction.Type
  | typeof VerifyCCPhoneAction.Type
  | ResetCCPhoneVerificationAction

  | typeof CCReplaceCardAction.Type
  | typeof CCLockCardAction.Type
  | typeof CCUnlockCardAction.Type
  | typeof ResetCCLockCardAction
  | typeof CCActivateCardAction.Type

  | typeof GetCCShipmentAddressAction.Type

  | typeof GetCCPaymentMethodsAction.Type
  | SetCCPaymentMethodsAction
  | typeof CreateCCPaymentMethodAction.Type
  | typeof DeleteCCPaymentMethodAction.Type
  | ResetCreateCCPaymentMethodAction
  | ResetDeleteCCPaymentMethodAction

  | typeof GetCCPaymentsAction.Type
  | typeof GetCCUpcomingPaymentsAction.Type
  | typeof CreateCCPaymentAction.Type
  | typeof DeleteCCPaymentAction.Type
  | ResetCreateCCPaymentAction
  | ResetDeleteCCPaymentAction
  | typeof CreateCCAutoPaymentAction.Type
  | typeof DeleteCCAutoPaymentAction.Type
  | ResetCreateCCAutoPaymentAction
  | ResetDeleteCCAutoPaymentAction
